import {
    PaymentTerminalBillingDetails,
    PaymentTerminalBillingDetailsSessionData,
    PaymentTerminalBillingDetailsSubmitData,
    PaymentTerminalBillingDetailsTaxIdLookupData,
    PaymentTerminalBillingDetailsTaxIdLookupRequest,
} from 'services/centralServerService/Api';
import { centralServerService } from 'services/centralServerService';
import {
    BusinessLookupRequest,
    BusinessResponse,
    BusinessSubmitRequest,
    BusinessTransportResult,
} from './contract';

export * from './contract';
export * from './canSubmit';
export * from './cui';
export * from './idempotency';
export * from './messages';
export * from './parseResponse';
export * from './retryPolicy';
export * from './restore';
export * from './submitRequest';

export type BillingSession = PaymentTerminalBillingDetailsSessionData & {
    /** null / absent: no business attempt yet. Never trust local storage instead. */
    businessState?: BusinessResponse | null;
    /** Read and validate the server's saved identity before replaying a pending submission. */
    businessSubmissionRecovery?: unknown;
};

/**
 * Replaceable transport for the billing page. `loadSession`, `lookup` and
 * `submit` are the Romanian B2B operations; `legacy*` are the unchanged
 * personal / skip-billing / non-RO flows.
 */
export interface BillingApi {
    loadSession(
        tenantId: string,
        token: string,
        verificationToken: string | null | undefined,
    ): Promise<BillingSession>;
    lookup(
        tenantId: string,
        token: string,
        request: BusinessLookupRequest,
    ): Promise<BusinessTransportResult>;
    submit(
        tenantId: string,
        token: string,
        request: BusinessSubmitRequest,
    ): Promise<BusinessTransportResult>;
    legacyLookup(
        tenantId: string,
        token: string,
        verificationToken: string | null | undefined,
        lookup: PaymentTerminalBillingDetailsTaxIdLookupRequest,
    ): Promise<PaymentTerminalBillingDetailsTaxIdLookupData>;
    legacySubmit(
        tenantId: string,
        token: string,
        verificationToken: string | null | undefined,
        billingDetails: PaymentTerminalBillingDetails,
    ): Promise<PaymentTerminalBillingDetailsSubmitData>;
}

const basePath = (tenantId: string, token: string) =>
    `/payment-terminal-attestation/${tenantId}/billing-details/${token}`;

/**
 * Sends a business request and resolves with the status and parsed JSON body
 * for EVERY HTTP status, so the envelope is read from 200 and non-2xx alike.
 * Network failures reject. A non-JSON body resolves with `body: undefined`.
 * Bypasses the global 5xx modal, which would hide structured errors.
 */
const sendBusinessRequest = async (
    path: string,
    body: unknown,
): Promise<BusinessTransportResult> => {
    const response = await centralServerService.http.instance.request({
        url: path,
        method: 'POST',
        data: body,
        headers: { 'Content-Type': 'application/json' },
        transformResponse: (data: unknown) => data,
        validateStatus: () => true,
    });
    let parsed: unknown;
    try {
        parsed =
            typeof response.data === 'string'
                ? JSON.parse(response.data)
                : response.data;
    } catch {
        parsed = undefined;
    }
    const retryAfter = response.headers?.['retry-after'];
    return {
        status: response.status,
        body: parsed,
        retryAfterHeader: typeof retryAfter === 'string' ? retryAfter : null,
    };
};

const realBillingApi: BillingApi = {
    loadSession: (tenantId, token, verificationToken) =>
        centralServerService.getPaymentTerminalBillingDetailsSession(
            tenantId,
            token,
            verificationToken,
        ),
    lookup: (tenantId, token, request) =>
        sendBusinessRequest(`${basePath(tenantId, token)}/tax-id/lookup`, request),
    submit: (tenantId, token, request) =>
        sendBusinessRequest(basePath(tenantId, token), request),
    legacyLookup: (tenantId, token, verificationToken, lookup) =>
        centralServerService.lookupPaymentTerminalBillingDetailsTaxId(
            tenantId,
            token,
            verificationToken,
            lookup,
        ),
    legacySubmit: (tenantId, token, verificationToken, billingDetails) =>
        centralServerService.submitPaymentTerminalBillingDetails(
            tenantId,
            token,
            verificationToken,
            billingDetails,
        ),
};

const createBillingApi = (): BillingApi => {
    // Build-time, development-only switch. The condition has to be written inline: in production builds NODE_ENV is
    // inlined, so webpack evaluates it to false and never bundles the mock module. Through a const it would keep it.
    if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_BILLING_MOCK === 'true') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const mock = require('./mock/mockTransport');
        return {
            loadSession: (tenantId, token, verificationToken) =>
                mock.mockLoadSession(tenantId, token, verificationToken),
            lookup: (_tenantId, _token, request) => mock.mockLookup(request),
            submit: (_tenantId, _token, request) => mock.mockSubmit(request),
            legacyLookup: (_tenantId, _token, _verificationToken, lookup) =>
                mock.mockLegacyLookup(lookup),
            legacySubmit: (_tenantId, _token, _verificationToken, billingDetails) =>
                mock.mockLegacySubmit(billingDetails),
        };
    }
    return realBillingApi;
};

export const billingApi: BillingApi = createBillingApi();
